import { ElectronicDocumentStatusHistory } from "../models/electronic-document-status-history";
import { SUCCESS } from "../utils/constants/tags";
import { getList } from "./generics";
import { getAbsoluteUrl } from "./urls";
import { fetchAuthenticated } from "./utils";

interface ElectronicDocumentHistoryParams {
  invoiceId?: number;
  creditNoteId?: number;
  debitNoteId?: number;
  remissionNoteId?: number;
  autoInvoiceId?: number;
  receiptId?: number;
  cdc?: string;
}

const getElectronicDocumentHistory = async (
  params: ElectronicDocumentHistoryParams,
): Promise<ElectronicDocumentStatusHistory[]> => {
  const searchParams: Map<string, string> = new Map();

  // Set a high limit to get all records for a document
  searchParams.set("limit", "1000");
  searchParams.set("offset", "0");

  // Receipts don't have CDC, keep searching by document ID for receipts only
  if (params.receiptId !== undefined) {
    searchParams.set("document_id", params.receiptId.toString());
    searchParams.set("document_type", "receipt");
  } else if (params.cdc !== undefined && params.cdc !== "") {
    // For all other electronic documents, search history by CDC
    searchParams.set("cdc", params.cdc);

    if (params.invoiceId !== undefined) {
      searchParams.set("document_type", "invoice");
    } else if (params.creditNoteId !== undefined) {
      searchParams.set("document_type", "credit_note");
    } else if (params.debitNoteId !== undefined) {
      searchParams.set("document_type", "debit_note");
    } else if (params.remissionNoteId !== undefined) {
      searchParams.set("document_type", "remission_note");
    } else if (params.autoInvoiceId !== undefined) {
      searchParams.set("document_type", "auto_invoice");
    }
  } else if (params.invoiceId !== undefined) {
    searchParams.set("document_id", params.invoiceId.toString());
    searchParams.set("document_type", "invoice");
  } else if (params.creditNoteId !== undefined) {
    searchParams.set("document_id", params.creditNoteId.toString());
    searchParams.set("document_type", "credit_note");
  } else if (params.debitNoteId !== undefined) {
    searchParams.set("document_id", params.debitNoteId.toString());
    searchParams.set("document_type", "debit_note");
  } else if (params.remissionNoteId !== undefined) {
    searchParams.set("document_id", params.remissionNoteId.toString());
    searchParams.set("document_type", "remission_note");
  } else if (params.autoInvoiceId !== undefined) {
    searchParams.set("document_id", params.autoInvoiceId.toString());
    searchParams.set("document_type", "auto_invoice");
  }

  const requestStatus = await getList<ElectronicDocumentStatusHistory>(
    "/electronic_document_status_history/",
    1000,
    0,
    searchParams,
  );

  if (requestStatus.status === SUCCESS && requestStatus.data !== undefined) {
    // Backend returns { records, count, limit, offset } structure
    const data = requestStatus.data as any;
    if (data.records && Array.isArray(data.records)) {
      return data.records;
    }
    if (data.items && Array.isArray(data.items)) {
      return data.items;
    }
  }

  return [];
};

const getElectronicDocumentHistoryRecord = async (
  id: number,
): Promise<ElectronicDocumentStatusHistory | null> => {
  try {
    const url = getAbsoluteUrl(`/electronic-document-status-history/${id}`);
    const response = await fetchAuthenticated(url, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const result = await response.json();
      if (result.status === SUCCESS && result.data) {
        return result.data;
      }
    }
  } catch (error) {
    console.error("Error fetching history record:", error);
  }

  return null;
};

export { getElectronicDocumentHistory, getElectronicDocumentHistoryRecord };
